import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IWaitlistState } from 'types/waitlist';
import { Error } from 'components/theme/Form/Form';
import { useAppState } from 'AppProvider';
import { Divider, Flex } from 'components/common/styled/common-styled';
import { SkeletonLine } from 'components/theme/Skeleton/SkeletonLine';
import {
  computeCartItemWithCompoundTaxes,
  MinimalCartItem,
} from '@kouto/cart-prices';
import { ExperienceVersion } from '@kouto/types';
import useListingV3 from 'api/listing/useListingV3/useListingV3';
import CartSummaryTotal from 'features/ShoppingCart/components/CartSummary/CartSummaryTotal';
import {
  adaptListingV3ForCart,
  adaptCartItemForCart,
} from 'utils/data/taxes/taxes';
import CartSummaryItem from '../CartSummary/CartSummaryItem';
import PriceSection from '../CartSummary/PriceSection';
import {
  BookSidebar,
  BookSticky,
  CartFooter,
  CartItemsContainer,
  CartSummaryTitle,
  WaitlistCartTotalSection,
} from '../CartSummary/styles';

const WaitlistCartSummary = () => {
  const { t } = useTranslation();
  const {
    item: waitlistCartItem,
    isFetchingExperience,
    waitlistError,
  }: IWaitlistState = useAppState(
    (state: Record<string, unknown>) => state.waitlist,
  );

  const {
    data: listing,
    error: errorListing,
    isLoading: isLoadingListing,
  } = useListingV3({
    listingId: waitlistCartItem?.listingId,
    skip: !waitlistCartItem?.listingId,
    version: ExperienceVersion.PUBLISHED,
  });

  const cartItemTotalDetails = useMemo(() => {
    if (!listing || !waitlistCartItem) return false;

    // Convert V3 listing to cart-compatible format
    const cartCompatibleListing = adaptListingV3ForCart(listing);

    // Convert cart item's addOns taxes to cart-compatible format
    const cartCompatibleItem = adaptCartItemForCart(waitlistCartItem);

    return computeCartItemWithCompoundTaxes(
      cartCompatibleItem as unknown as MinimalCartItem,
      cartCompatibleListing,
    );
  }, [waitlistCartItem, listing?.id]);

  if (!waitlistCartItem) {
    return (
      <BookSticky>
        <BookSidebar>
          <div>The cart is empty</div>
        </BookSidebar>
      </BookSticky>
    );
  }

  if (isFetchingExperience || isLoadingListing) {
    return (
      <BookSticky>
        <SkeletonLine style={{ height: 300 }} translucent />
      </BookSticky>
    );
  }

  if (errorListing || !cartItemTotalDetails) {
    return (
      <BookSticky>
        <BookSidebar>
          <Error>There was an error while fetching the experience.</Error>
        </BookSidebar>
      </BookSticky>
    );
  }

  return (
    <BookSticky>
      <BookSidebar>
        <CartSummaryTitle>
          <Flex direction="row">
            <span>{t('reservationSummary')}</span> <span>(1)</span>
          </Flex>
          {waitlistError && (
            <div style={{ paddingTop: '10px' }}>
              <Error>
                There seem to be an error while fetching the waitlist.
              </Error>
            </div>
          )}
        </CartSummaryTitle>
        <CartItemsContainer>
          {!!cartItemTotalDetails && (
            <CartSummaryItem
              cartItem={waitlistCartItem}
              listing={listing}
              cartItemTotalDetails={cartItemTotalDetails}
              canDelete={false}
              canEditParticipants={false}
            />
          )}
        </CartItemsContainer>

        <CartFooter>
          <WaitlistCartTotalSection>
            <PriceSection
              cartSubTotal={cartItemTotalDetails.subTotal}
              cartTotal={cartItemTotalDetails.total}
              includedTaxes={cartItemTotalDetails.includedTaxes}
              nonIncludedTaxes={cartItemTotalDetails.nonIncludedTaxes}
            />
            <Divider space={0} style={{ marginBottom: '20px' }} />
            <CartSummaryTotal cartTotal={cartItemTotalDetails.total} />
          </WaitlistCartTotalSection>
        </CartFooter>
      </BookSidebar>
    </BookSticky>
  );
};

export default WaitlistCartSummary;
